import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react';
import OAuthButtons from '../components/auth/OAuthButtons';
import AccountRecoveryFlow from '../components/auth/AccountRecoveryFlow';
import AuthStage from '../components/auth/AuthStage';
import PhoneAppOffers from '../components/auth/PhoneAppOffer';
import PikBrand from '../components/brand/PikBrand';
import { PikLogo } from '../components/brand/PikLogo';
import { hardNavigate } from '../lib/hardNavigate';
import { postSignInPath } from '../lib/shareContinue';
import { scanDraftEmail } from '../lib/scanSignupDraft';
import { PHONE_APP_COPY, emailLooksCaptured, usePhoneAppOffer } from '../lib/phoneAppOffer';

function readMagicLinkReturn() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('magiclink') === '1';
}

export default function LoginPage() {
  const { login, completeMfa } = useAuth();
  // Navigation after login is a hard `window.location.replace()` (see
  // login flow below) so we don't need react-router's `navigate`.
  const [email, setEmail] = useState(() => scanDraftEmail());
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  // Default the "Stay logged in for 30 days" checkbox to ON. Users were
  // hitting unexpected ~3-day logouts because the normal-session cookie
  // expires after JWT_EXP_HOURS (72h). Almost everyone signing in from a
  // personal device wants the long-lived session — and the box is right
  // there if a public-computer user wants to opt out.
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(null);

  // MFA challenge state — populated when /auth/login replies with mfa_required.
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetStep, setResetStep] = useState(1);

  // Email step, then method choice, then password. The magic-link
  // request fires only from the deliberate method-step action.
  const [signStep, setSignStep] = useState(() => (
    readMagicLinkReturn() && emailLooksCaptured(scanDraftEmail()) ? 'method' : 'email'
  ));
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  // The in-flight magic-link request may resolve after Change, a new email,
  // or a newer send. Only the generation that started it may update this screen.
  const magicScopeRef = useRef({ generation: 0, email: '' });
  const signStepRef = useRef('email');
  const normalizedEmailRef = useRef('');
  const [emailError, setEmailError] = useState('');
  const [linkExpired, setLinkExpired] = useState(readMagicLinkReturn);
  const [iosWaitlist, setIosWaitlist] = useState(false);
  const [androidWaitlist, setAndroidWaitlist] = useState(false);
  const phoneOffer = usePhoneAppOffer();

  // Account recovery state (Session 124f — "Can't access your email?")
  const [showRecovery, setShowRecovery] = useState(false);

  // Surface ?oauth_error=... from the provider callback flow.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('oauth_error');
    if (err) {
      toast.error(`Sign-in failed: ${err}`);
      params.delete('oauth_error');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? '?' + qs : ''}`);
    }
    // ?magiclink=1 — the landing page sent an expired or used link here.
    // Offer a fresh request. Do not send one until the person asks.
    if (params.get('magiclink') === '1') {
      setLinkExpired(true);
      if (emailLooksCaptured(scanDraftEmail())) setSignStep('method');
    }
    // ?mfa=1&mfa_token=… — a session-issuing path (OAuth callback,
    // magic-link, password reset, verify-to-link) that requires the second
    // factor bounced here. Open the SAME MFA challenge used by password
    // login; the user finishes via completeMfa() → /auth/mfa/verify.
    if (params.get('mfa') === '1' && params.get('mfa_token')) {
      setMfaToken(params.get('mfa_token'));
      setMfaError('');
      // Strip the token from the address bar so a refresh / share doesn't
      // leak or replay it.
      params.delete('mfa');
      params.delete('mfa_token');
      params.delete('provider');
      const qs2 = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs2 ? '?' + qs2 : ''}`);
    }
  }, []);
  const [resetLoading, setResetLoading] = useState(false);

  const normalizedEmail = (email || '').trim().toLowerCase();
  signStepRef.current = signStep;
  normalizedEmailRef.current = normalizedEmail;

  const magicRequestIsCurrent = (generation, requestEmail) => {
    const scope = magicScopeRef.current;
    return scope.generation === generation
      && scope.email === requestEmail
      && normalizedEmailRef.current === requestEmail
      && signStepRef.current === 'method';
  };

  // Drop an in-flight send. clearConfirmation is for leaving this email's
  // method screen. A finished confirmation can stay while the person looks
  // at the password step for the same address.
  const retireMagicRequest = (clearConfirmation) => {
    magicScopeRef.current = {
      generation: magicScopeRef.current.generation + 1,
      email: '',
    };
    setMagicLoading(false);
    if (clearConfirmation) setMagicSent(false);
  };

  useEffect(() => () => {
    magicScopeRef.current = {
      generation: magicScopeRef.current.generation + 1,
      email: '',
    };
  }, []);

  const continueWithEmail = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!emailLooksCaptured(normalizedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError('');
    setLoginError('');
    signStepRef.current = 'method';
    retireMagicRequest(true);
    setSignStep('method');
  };

  const changeEmail = () => {
    signStepRef.current = 'email';
    retireMagicRequest(true);
    setPassword('');
    setShowPw(false);
    setLoginError('');
    setEmailError('');
    setSignStep('email');
  };

  const handleRequestMagicLink = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (magicLoading || magicSent) return;
    const requestEmail = normalizedEmail;
    if (!emailLooksCaptured(requestEmail)) {
      setEmailError('Enter a valid email address.');
      signStepRef.current = 'email';
      setSignStep('email');
      return;
    }
    const generation = magicScopeRef.current.generation + 1;
    magicScopeRef.current = { generation, email: requestEmail };
    setMagicLoading(true);
    setMagicSent(false);
    try {
      await authAPI.requestMagicLink(requestEmail);
      if (!magicRequestIsCurrent(generation, requestEmail)) return;
      setMagicSent(true);
    } catch (err) {
      if (!magicRequestIsCurrent(generation, requestEmail)) return;
      setMagicSent(false);
      toast.error(err?.response?.data?.detail || 'Could not send sign-in link');
    } finally {
      if (magicRequestIsCurrent(generation, requestEmail)) setMagicLoading(false);
    }
  };

  // Check if locked out
  const isLocked = lockedUntil && Date.now() < lockedUntil;
  const lockRemaining = isLocked ? Math.ceil((lockedUntil - Date.now()) / 60000) : 0;

  const handleSubmit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    
    if (!email || !password) {
      setLoginError('Please enter email and password.');
      return;
    }
    
    if (isLocked) {
      setLoginError(`Account locked. Try again in ${lockRemaining} minute${lockRemaining !== 1 ? 's' : ''}.`);
      return;
    }
    
    setLoginError('');
    setLoading(true);
    try {
      // Normalize email client-side so a stray iOS autocapitalize on the
      // first character doesn't cause "Invalid email or password" even
      // before the request leaves the device. (Backend now matches
      // case-insensitively too — this is defense in depth.)
      const normEmail = (email || '').trim().toLowerCase();
      const result = await login(normEmail, password, { remember_me: rememberMe });
      if (result?.mfa_required) {
        // Hand off to MFA challenge UI.
        setMfaToken(result.mfa_token);
        setMfaError('');
        setLoading(false);
        return;
      }
      const user = result;
      setAttempts(0);
      toast.success(`Welcome back, ${user.username}!`);
      // Hard-replace navigation (not SPA-routed) so the browser performs
      // a fresh top-level GET — guarantees the just-set httpOnly cookie
      // is attached on every request the new page fires. SPA navigation
      // sometimes raced the cookie store on iOS Safari, causing the
      // post-login Home to 401 and bounce the user back to /login.
      // Pending Save-to-PeopleiKnow scan context wins over Home/onboarding.
      const next = new URLSearchParams(window.location.search).get('next') || '';
      hardNavigate(postSignInPath(user, { next }));
    } catch (err) {
      // Only a real 401 from the backend means the credentials were wrong.
      // A 5xx / network error (e.g. the backend pod restarting) means the
      // server was unreachable -- that must NOT count as a failed attempt, or
      // a transient outage trips the 5-attempt lockout and locks the user out
      // of their own account despite a correct password.
      const status = err?.response?.status;
      if (status !== 401) {
        setLoginError(
          "We couldn't reach the server. Please check your connection and try again in a moment."
        );
        setLoading(false);
        return;
      }

      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= 5) {
        const lockTime = Date.now() + 5 * 60 * 1000;
        setLockedUntil(lockTime);
        setLoginError('Too many failed attempts. Account locked for 5 minutes.');
      } else if (newAttempts === 4) {
        setLoginError('Incorrect email or password. You have 1 more attempt before lockout.');
      } else {
        setLoginError('Incorrect email or password. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!mfaCode.trim()) {
      setMfaError('Enter the 6-digit code from your authenticator app, or a recovery code.');
      return;
    }
    setMfaLoading(true);
    setMfaError('');
    try {
      const data = await completeMfa({ mfa_token: mfaToken, code: mfaCode.trim() });
      setMfaLoading(false);
      const user = data.user;
      // Section 3 — last recovery code just spent. The backend handed back a
      // short-lived repair_token. Stash it in sessionStorage (survives the
      // cookie hard-navigation; NOT localStorage, NOT the URL) and land on the
      // 2FA screen with a blocking repair prompt. Cleared there on success.
      if (data.repair_token) {
        try { sessionStorage.setItem('pik_mfa_repair', data.repair_token); } catch { /* private mode */ }
        toast.warning('That was your last recovery code. Set up fresh codes or replace your authenticator now.');
        hardNavigate('/settings/2fa');
        return;
      }
      if (data.used_recovery_code) {
        toast.warning(
          `You used a recovery code (${data.recovery_codes_remaining} remaining). Generate a fresh set under Settings → Security.`
        );
      } else {
        toast.success(`Welcome back, ${user.username}!`);
      }
      // Same hard-replace as the password path — see comment above.
      // MFA repair stays /settings/2fa even when a share token is stashed.
      const next = new URLSearchParams(window.location.search).get('next') || '';
      hardNavigate(postSignInPath(user, { next }));
    } catch (err) {
      setMfaError(err.response?.data?.detail || 'Invalid code. Please try again.');
      setMfaLoading(false);
    }
  };

  const cancelMfaChallenge = () => {
    setMfaToken(null);
    setMfaCode('');
    setMfaError('');
    setMfaLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setResetLoading(true);
    try {
      // Session 124i — link-based reset is the primary UX. Backend
      // returns a generic 200 either way (anti-enumeration), so we
      // always advance to the "check your inbox" confirmation step.
      await authAPI.forgotPasswordLink((forgotEmail || '').trim().toLowerCase());
      toast.success('If that email is registered, a reset link is on its way.');
      setResetStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset link');
    }
    setResetLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetLoading(true);
    try {
      // Backend currently uses `code` (not `reset_code`). The api helper
      // already maps for us, but normalize the email to lowercase first
      // so a stray autocapitalize on iOS doesn't cause an "invalid request"
      // (the matching is case-insensitive on the server now, but this is
      // defence-in-depth and matches the login flow).
      const { data } = await authAPI.resetPassword((forgotEmail || '').trim().toLowerCase(), resetCode, newPassword);
      // MFA gate — a reset must not bypass the second factor. Hand off to the
      // challenge instead of auto-login.
      if (data?.mfa_required) {
        setShowForgotPassword(false);
        setMfaToken(data.mfa_token);
        setMfaError('');
        toast.success('Password reset. One more step to sign in.');
        setResetLoading(false);
        return;
      }
      // Backend now issues an auth cookie on successful reset. Hard-navigate
      // straight into the app — same pattern as the login flow — so the
      // user doesn't have to retype the password they just chose.
      if (data?.auto_login) {
        toast.success('Password reset! Signing you in...');
        // Brief delay so the toast lands before the page tears down.
        setTimeout(() => { hardNavigate('/'); }, 250);
        return;
      }
      // Defensive fallback (older backend / unexpected shape): close the
      // dialog and ask the user to sign in manually.
      toast.success('Password reset successfully! Please login.');
      setShowForgotPassword(false);
      setResetStep(1);
      setForgotEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
    setResetLoading(false);
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setShowRecovery(false);
    setResetStep(1);
    setForgotEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const openForgotPassword = () => {
    signStepRef.current = 'password';
    retireMagicRequest(false);
    setForgotEmail((email || '').trim());
    setShowRecovery(false);
    setResetStep(1);
    setShowForgotPassword(true);
  };

  const inputCls = 'h-12 bg-slate-50 border-transparent focus:border-nav-blue focus:bg-white transition-all rounded-lg';
  const primaryBtn = 'w-full h-12 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full font-heading font-semibold text-base shadow-lg shadow-nav-blue/20 transition-all';

  return (
    <AuthStage testId="login-page">
      <div className="flex flex-col gap-8" data-testid="login-shell-stack">
        <div className="text-center space-y-3">
          <PikLogo className="h-24" testId="login-pik-logo" />
          <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">
            <PikBrand testId="login-pik-brand" />
          </h1>
          <p className="text-muted-foreground text-sm">Your contacts reimagined, organized, up to date.</p>
        </div>

        {showForgotPassword ? (
          showRecovery ? (
            <AccountRecoveryFlow onBack={() => setShowRecovery(false)} />
          ) : (
          <div className="space-y-4" data-testid="forgot-password-form">
            <button
              onClick={closeForgotPassword}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-login"
            >
              <ArrowLeft size={16} />
              Back to login
            </button>

            <div className="text-center space-y-1">
              <h2 className="font-heading text-xl font-bold">Reset Password</h2>
              <p className="text-sm text-muted-foreground">
                {resetStep === 1 ? 'Enter your email to receive a reset link' : `We sent a reset link to ${forgotEmail.toLowerCase()}`}
              </p>
            </div>

            {resetStep === 1 ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className={inputCls}
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full h-12 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full font-heading font-semibold"
                  data-testid="send-reset-code-btn"
                >
                  {resetLoading ? 'Sending...' : 'Email me a reset link'}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowRecovery(true)}
                  className="w-full text-sm text-nav-blue hover:underline pt-1"
                  data-testid="cant-access-email-link"
                >
                  Can&apos;t access your email anymore?
                </button>
              </form>
            ) : (
              <div className="space-y-4" data-testid="reset-link-sent-confirmation">
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-4 rounded-lg space-y-2">
                  <div className="flex items-center gap-2 font-semibold">
                    <ShieldCheck size={16} /> Check your inbox
                  </div>
                  <p className="text-xs">
                    Tap the &ldquo;Reset my password&rdquo; button in the email we just sent. The link is valid for 15 minutes.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() => setResetStep(1)}
                  variant="outline"
                  className="w-full h-12 rounded-full font-heading font-semibold"
                  data-testid="resend-reset-link-btn"
                >
                  Didn&apos;t get it? Try again
                </Button>
                <button
                  type="button"
                  onClick={closeForgotPassword}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="reset-link-back-to-login"
                >
                  Back to sign in
                </button>
              </div>
            )}
          </div>
          )
        ) : mfaToken ? (
          <form className="space-y-4" data-testid="mfa-challenge-form" onSubmit={handleMfaSubmit}>
            <button
              type="button"
              onClick={cancelMfaChallenge}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="mfa-cancel-btn"
            >
              <ArrowLeft size={16} />
              Use a different account
            </button>

            <div className="text-center space-y-2">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <ShieldCheck size={22} />
              </div>
              <h2 className="font-heading text-xl font-bold">Two-factor authentication</h2>
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app — or a recovery code.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mfa-code" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                Authenticator code
              </Label>
              <Input
                id="mfa-code"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                placeholder="123456 or XXXXX-XXXXX"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                autoFocus
                className={`${inputCls} text-center tracking-widest text-lg`}
                data-testid="mfa-code-input"
              />
              <p className="text-xs text-muted-foreground text-center" data-testid="mfa-recovery-hint">
                Lost your authenticator? Enter one of your{' '}
                <span className="font-semibold text-foreground">recovery codes</span> (XXXXX-XXXXX) here instead.
              </p>
            </div>

            {mfaError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg" data-testid="mfa-error-message">
                {mfaError}
              </div>
            )}

            <Button
              type="submit"
              disabled={mfaLoading}
              className={primaryBtn}
              data-testid="mfa-submit-btn"
            >
              {mfaLoading ? 'Verifying…' : 'Verify and sign in'}
            </Button>

            {rememberMe && (
              <p className="text-center text-xs text-muted-foreground">
                This device will stay signed in for 30 days.
              </p>
            )}
          </form>
        ) : (
          <>
            <Link
              to="/signup"
              className="block"
              data-testid="login-create-account-top-cta"
            >
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 px-4 rounded-full font-heading font-semibold text-base border-2 border-nav-blue text-nav-blue hover:bg-nav-blue/5 transition-all"
              >
                Create a free account
              </Button>
            </Link>

            <div className="flex items-center gap-3" data-testid="login-account-divider">
              <div className="flex-1 h-px bg-border" />
              <span className="font-heading text-[17px] font-bold tracking-tight text-foreground whitespace-nowrap">Have an account? Sign in.</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {linkExpired && !magicSent && (
              <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3 rounded-lg" data-testid="magic-link-expired-note">
                That sign-in link has expired or was already used. You can request a new one.
              </div>
            )}

            <div className={signStep === 'email' ? 'flex flex-col gap-5' : 'hidden'} data-testid="login-email-step">
              <OAuthButtons
                mode="signin"
                showDivider={false}
                providerOrder={['apple', 'google']}
              />
              <div className="flex items-center gap-3" data-testid="login-or-divider">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-sm font-extrabold tracking-widest text-foreground">-OR-</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
              <form noValidate onSubmit={continueWithEmail} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-bold text-foreground">Sign in with email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); continueWithEmail(e); } }}
                    className={inputCls}
                    data-testid="login-email-input"
                  />
                </div>
                {emailError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg" data-testid="login-email-error">
                    {emailError}
                  </div>
                )}
                <Button
                  type="submit"
                  className={primaryBtn}
                  data-testid="login-email-continue-btn"
                >
                  Sign In
                </Button>
              </form>
            </div>

            {signStep === 'method' && (
              <div className="flex flex-col gap-4" data-testid="login-method-step">
                <p className="text-sm text-foreground break-all" data-testid="login-signing-in-as">
                  Signing in as <strong>{email.trim()}</strong>{' '}
                  <button
                    type="button"
                    onClick={changeEmail}
                    className="text-nav-blue font-semibold hover:underline whitespace-nowrap"
                    data-testid="login-change-email-btn"
                  >
                    Change
                  </button>
                </p>
                {magicSent ? (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 text-sm px-3 py-3 rounded-lg" data-testid="magic-link-sent-confirmation">
                    If that email is registered, a sign-in link is on its way. The link expires in 15 minutes and can be used once.
                  </div>
                ) : (
                  <Button
                    type="button"
                    onClick={handleRequestMagicLink}
                    disabled={magicLoading}
                    className={primaryBtn}
                    data-testid="login-magic-link-btn"
                  >
                    <ShieldCheck size={16} className="mr-2" />
                    {magicLoading ? 'Sending…' : 'Sign in with magic link'}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    signStepRef.current = 'password';
                    retireMagicRequest(false);
                    setLoginError('');
                    setSignStep('password');
                  }}
                  className="w-full h-12 rounded-full font-heading font-semibold text-base border-2 border-nav-blue text-nav-blue hover:bg-nav-blue/5"
                  data-testid="login-use-password-btn"
                >
                  Use password instead
                </Button>
              </div>
            )}

            {signStep === 'password' && (
              <form noValidate onSubmit={handleSubmit} className="space-y-4" data-testid="login-password-step">
                <p className="text-sm text-foreground break-all" data-testid="login-signing-in-as">
                  Signing in as <strong>{email.trim()}</strong>{' '}
                  <button
                    type="button"
                    onClick={changeEmail}
                    className="text-nav-blue font-semibold hover:underline whitespace-nowrap"
                    data-testid="login-change-email-btn"
                  >
                    Change
                  </button>
                </p>
                <input
                  type="email"
                  name="username"
                  autoComplete="username"
                  value={email}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                />
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPw ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }}
                      className={`${inputCls} pr-10`}
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                      data-testid="login-password-toggle"
                    >
                      {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {loginError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg" data-testid="login-error-message">
                    {loginError}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading || isLocked}
                  className={primaryBtn}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Signing in...' : isLocked ? `Locked (${lockRemaining}m)` : 'Sign In'}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    signStepRef.current = 'method';
                    setLoginError('');
                    setPassword('');
                    setShowPw(false);
                    setSignStep('method');
                  }}
                  className="w-full text-sm font-semibold text-nav-blue hover:underline"
                  data-testid="login-back-to-magic-btn"
                >
                  Sign in with magic link
                </button>
              </form>
            )}

            {/* Below the sign-in options. Password login sends remember_me.
                Magic link and Google/Apple do not read it yet. */}
            <div className="flex items-center justify-between gap-3 pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(v) => setRememberMe(Boolean(v))}
                  data-testid="remember-me-checkbox"
                />
                <span className="text-sm text-foreground/80">Stay logged in for 30 days</span>
              </label>
              {signStep === 'password' && (
                <button
                  type="button"
                  onClick={openForgotPassword}
                  className="text-sm text-nav-blue hover:underline shrink-0"
                  data-testid="login-forgot-password-btn"
                >
                  Forgot password?
                </button>
              )}
            </div>
          </>
        )}

        <div className={mfaToken ? 'hidden' : 'space-y-6'}>
          <div className="space-y-3" data-testid="login-waitlist">
            <p className="text-sm font-medium text-foreground">{PHONE_APP_COPY.comingSoon}</p>
            <PhoneAppOffers
              variant="login"
              ready={phoneOffer.ready}
              appStoreUrl={phoneOffer.appStoreUrl}
              playStoreUrl={phoneOffer.playStoreUrl}
              iosJoined={iosWaitlist}
              androidJoined={androidWaitlist}
              emailDialog
              formEmail={email}
              onIosChange={setIosWaitlist}
              onAndroidChange={setAndroidWaitlist}
            />
          </div>
          <p className="text-center text-sm text-muted-foreground" data-testid="login-learn-more">
            By continuing, you are agreeing to our <a href="https://peopleiknow.ai/terms" className="text-nav-blue font-semibold hover:underline">Terms of Service</a> and <a href="https://peopleiknow.ai/privacy" className="text-nav-blue font-semibold hover:underline">Privacy Policy</a>. Learn more at <a href="https://www.peopleiknow.ai" className="text-nav-blue font-semibold hover:underline">www.peopleiknow.ai</a>.
          </p>
        </div>
      </div>
    </AuthStage>
  );
}
